'use strict';

import './VZQuantitySelector';
import './VZVariationAttribute';
import './VZPorting';
import './VZAdditionalProduct';
import './VZRoaming';
import './VZPCAddonManager';

class VZProductCard extends HTMLElement {
    constructor() {
        super();
        this.sidebar = document.querySelector('.js-configurator-sidebar');
        this.isOneNet = this.dataset.configuratorType === 'one-net';
        this.isBusinessIct = this.dataset.configuratorType === 'business-ict';
        this.hardwarePhoneThresholdBtn = document.querySelector('.js-hardware-phone-treshold-btn');
    }

    static updateJourneyModal(fragment) {
        const journeyModal = document.querySelector('vz-journey-modal');
        if (journeyModal) {
            journeyModal.replaceWith(fragment.content.querySelector('vz-journey-modal'));
        }
    }

    static updateChatInfo(fragment) {
        // Update the hidden input field for chat info
        const activeConfiguratorGroup = document.querySelector('#activeConfiguratorGroup');
        if (activeConfiguratorGroup) {
            activeConfiguratorGroup.value = fragment.content.querySelector('#activeConfiguratorGroup').value;

            // Manually dispatch change event as programmatic changes to an input field don't natively trigger an event
            const event = new Event('change');
            activeConfiguratorGroup.dispatchEvent(event);
        }
    }

    /**
     * Updates the Business ICT submit buttons from the current rendered selections.
     * This avoids leaking temporary submit state through vz-product-card attributes,
     * which are reused by the variation URL builders.
     */
    syncBusinessIctSubmitButtons() {
        if (!this.isBusinessIct) {
            return;
        }

        const workplaceSelection = this.querySelector('input[name="tier"]:checked');
        const securitySelection = this.querySelector('input[name="ictSecurityTier"]:checked');
        const hasWorkplaceTierSelected = !!(workplaceSelection && !workplaceSelection.id.endsWith('-none'));
        const hasSecurityTierSelected = !!(securitySelection && securitySelection.id !== 'ictSecurityTierNone');
        const shouldDisableSubmit = !hasWorkplaceTierSelected && !hasSecurityTierSelected;

        [...document.querySelectorAll('.js-configurator-submit-btn, .js-page-configurator-submit-btn')].forEach((button) => {
            button.classList.toggle('opacity-50', shouldDisableSubmit);
            button.classList.toggle('pe-none', shouldDisableSubmit);
            button.toggleAttribute('disabled', shouldDisableSubmit);
        });
    }

    connectedCallback() {
        this.addEventListener('variationChange', this.onVariationChange);
        this.hardwarePhoneThresholdBtn?.addEventListener('click', VZProductCard.removeShowMoreBtn.bind(this));
        this.syncBusinessIctSubmitButtons();
    }

    disconnectedCallback() {
        this.hardwarePhoneThresholdBtn?.removeEventListener('click', VZProductCard.removeShowMoreBtn.bind(this));
    }

    onVariationChange(evt) {
        const { response } = evt.detail;
        const fragment = document.createElement('template');
        fragment.innerHTML = response;
        this.updateCard(fragment.content.querySelector('vz-product-card').outerHTML);
        this.updateSideBar(fragment);
        this.checkOneNetSeats(fragment);
        this.constructor.updateJourneyModal(fragment);
        this.constructor.updateChatInfo(fragment);
    }

    updateCard(productCardHTML) {
        if (this.outerHTML) this.outerHTML = productCardHTML;
    }

    updateSideBar(fragment) {
        this.sidebar.dispatchEvent(
            new CustomEvent('variationChange', {
                detail: {
                    fragment: fragment
                }
            })
        );

        // Set the postcode on the current configuration
        const postCodeCheck = document.querySelector('[is="vz-postcode-check"]');
        const MACDconfigurator = document.querySelector('.js-macd-configurator');
        if (postCodeCheck && !MACDconfigurator) {
            postCodeCheck.setAddress();
        }
    }

    checkOneNetSeats(fragment) {
        if (this.isOneNet) {
            // Check if the contract duration is selected while disabled because quantity is below threshold
            const contractDuration = document.querySelector('input[id="24"][name="contractDuration"]');
            if (contractDuration && contractDuration.checked && contractDuration.disabled) {
                document.querySelector('input:not([disabled])[name="contractDuration"]').click();
                return;
            }

            // If one-net, and no seat is selected OR the number of seatsSelected is below seatsThreshold, dispatch an event to disable the configurator.
            const { shouldDisableSubmit } = fragment.content.querySelector('[data-should-disable-submit]').dataset;
            const allQuantitySelectors = [...this.querySelectorAll('.js-quantity-field')];
            const combinedQuantity = allQuantitySelectors.reduce((total, currentElement) => total + parseInt(currentElement.value, 10), 0);

            document.dispatchEvent(
                new CustomEvent('oneNet:seatSelection', {
                    detail: {
                        isOneNetSeatSelected: combinedQuantity > 0,
                        areOneNetSeatsBelowThreshold: shouldDisableSubmit && shouldDisableSubmit === 'true'
                    }
                })
            );

            if (fragment.content.querySelector('.js-pdp-error')) {
                [...document.querySelectorAll('.js-pdp-submit-button button')].forEach((button) => {
                    button.classList.add('pe-none', 'opacity-50');
                    button.setAttribute('disabled', '');
                });
            }
        }
    }

    /**
     * Removes the Hardware Phones show more button when clicked
     * @param {Event} e - The event object.
     */
    static removeShowMoreBtn(e) {
        e.target.parentElement.remove();
    }
}

export default window.customElements.get('vz-product-card') || VZProductCard;

if (!window.customElements.get('vz-product-card')) {
    window.customElements.define('vz-product-card', VZProductCard);
}
